import { collection, addDoc, doc, setDoc, deleteDoc, query, where, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { firestore, storage } from './firebase';
import { DatabaseSoundPack } from './DatabaseSoundPack';

// One post in the plugin's News tab. The kind decides which fields it has,
// where its date comes from and what clicking it opens:
//   pack   — about a sound pack; dated by the pack, opens its info page
//   update — a software release; dated by the release, opens the website's
//            account page (downloads)
//   other  — anything else; dated by the admin, optional external link
export type NewsEntryKind = 'pack' | 'update' | 'other';

type NewsEntryBase = {
    id?: string; // Document ID from Firestore
    title: string; // headline; "\n" renders as a line break
    public: boolean; // true = visible to all users, false = admins only
    body?: string | null; // secondary line under the title
    blurb?: string | null; // short story paragraph, collapsed to 3 lines
    imageUrl?: string | null; // Cloud Storage download URL
};

export type PackNewsEntry = NewsEntryBase & {
    kind: 'pack';
    packId: string;
};

export type UpdateNewsEntry = NewsEntryBase & {
    kind: 'update';
    version: string; // a released version (settings/versions), e.g. "2.1.0"
    changelog: string[]; // one change per line
};

export type OtherNewsEntry = NewsEntryBase & {
    kind: 'other';
    createdAt: string; // ISO 8601
    link?: string | null; // external link
};

export type DatabaseNewsEntry = PackNewsEntry | UpdateNewsEntry | OtherNewsEntry;

// An entry with its date resolved (see withDates); the shape the News tab
// renders. The date drives ordering, the displayed date and the "New" badge.
export type DatedNewsEntry = DatabaseNewsEntry & { createdAt: string };

// Field name constants (kept private to this file)
class FieldPaths {
    static readonly TITLE = "title";
    static readonly CREATED_AT = "createdAt";
    static readonly PUBLIC = "public";
    static readonly KIND = "kind";
    static readonly BODY = "body";
    static readonly BLURB = "blurb";
    static readonly VERSION = "version";
    static readonly CHANGELOG = "changelog";
    static readonly IMAGE_URL = "imageUrl";
    static readonly PACK_ID = "packId";
    static readonly LINK = "link";
}

export class DatabaseNewsEntryModel {

    static readonly COLLECTION_NAME = "news";

    private static get _newsCollection() {
        return collection(firestore, this.COLLECTION_NAME);
    }

    /**
     * An entry's date, by kind: the pack's createdAt, the version's release
     * date, or the entry's own createdAt. Null when the source isn't available
     * (e.g. a pack the user can't see, or a version that was never released).
     * @param releaseDates version → ISO release timestamp (settings/versions)
     */
    static resolveDate(
        entry: DatabaseNewsEntry,
        packs: DatabaseSoundPack[],
        releaseDates: Record<string, string>,
    ): string | null {
        switch (entry.kind) {
            case 'pack': return packs.find(p => p.id === entry.packId)?.createdAt || null;
            case 'update': return releaseDates[entry.version] ?? null;
            case 'other': return entry.createdAt;
        }
    }

    /**
     * Resolves every entry's date (see resolveDate), drops the ones without
     * one, and sorts the rest newest-first.
     */
    static withDates(
        entries: DatabaseNewsEntry[],
        packs: DatabaseSoundPack[],
        releaseDates: Record<string, string>,
    ): DatedNewsEntry[] {
        return entries
            .flatMap((entry): DatedNewsEntry[] => {
                const createdAt = this.resolveDate(entry, packs, releaseDates);
                return createdAt ? [{ ...entry, createdAt }] : [];
            })
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    }

    static fromFirestore(data: any, docId?: string): DatabaseNewsEntry {
        if (
            typeof data !== "object" ||
            !data ||
            typeof data[FieldPaths.TITLE] !== "string" ||
            typeof data[FieldPaths.PUBLIC] !== "boolean"
        ) {
            throw new Error("Invalid news entry document structure");
        }
        const base: NewsEntryBase = {
            id: docId,
            title: data[FieldPaths.TITLE],
            public: data[FieldPaths.PUBLIC],
            body: data[FieldPaths.BODY] || null,
            blurb: data[FieldPaths.BLURB] || null,
            imageUrl: data[FieldPaths.IMAGE_URL] || null,
        };
        const kind = data[FieldPaths.KIND];
        if (kind === 'pack' && typeof data[FieldPaths.PACK_ID] === 'string') {
            return { ...base, kind, packId: data[FieldPaths.PACK_ID] };
        }
        if (kind === 'update' && typeof data[FieldPaths.VERSION] === 'string') {
            const changelog = data[FieldPaths.CHANGELOG];
            return {
                ...base,
                kind,
                version: data[FieldPaths.VERSION],
                changelog: Array.isArray(changelog) ? changelog.filter((line: unknown): line is string => typeof line === 'string') : [],
            };
        }
        if (kind === 'other' && typeof data[FieldPaths.CREATED_AT] === 'string') {
            return { ...base, kind, createdAt: data[FieldPaths.CREATED_AT], link: data[FieldPaths.LINK] || null };
        }
        throw new Error(`Invalid news entry: kind "${kind}" is unknown or missing its required field`);
    }

    // Only the fields of the entry's kind (optional ones only when set) —
    // updateNewsEntry replaces the whole document, so a cleared optional field
    // is removed.
    static toFirestore(entry: DatabaseNewsEntry): any {
        const data: any = {
            [FieldPaths.TITLE]: entry.title,
            [FieldPaths.PUBLIC]: entry.public,
            [FieldPaths.KIND]: entry.kind,
        };
        if (entry.body) data[FieldPaths.BODY] = entry.body;
        if (entry.blurb) data[FieldPaths.BLURB] = entry.blurb;
        if (entry.imageUrl) data[FieldPaths.IMAGE_URL] = entry.imageUrl;
        switch (entry.kind) {
            case 'pack':
                data[FieldPaths.PACK_ID] = entry.packId;
                break;
            case 'update':
                data[FieldPaths.VERSION] = entry.version;
                data[FieldPaths.CHANGELOG] = entry.changelog;
                break;
            case 'other':
                data[FieldPaths.CREATED_AT] = entry.createdAt;
                if (entry.link) data[FieldPaths.LINK] = entry.link;
                break;
        }
        return data;
    }

    // ====================== ADMIN API METHODS ============================

    /**
     * Create a news entry (the id field is ignored; Firestore assigns one).
     * @returns Promise<string> The document ID of the created entry
     */
    static async createNewsEntry(entry: DatabaseNewsEntry): Promise<string> {
        const docRef = await addDoc(this._newsCollection, this.toFirestore(entry));
        console.log(`Created news entry "${entry.title}", id = ${docRef.id}`);
        return docRef.id;
    }

    /** Replace an existing news entry (fields not in `entry` are removed). */
    static async updateNewsEntry(entryId: string, entry: DatabaseNewsEntry): Promise<void> {
        await setDoc(doc(firestore, this.COLLECTION_NAME, entryId), this.toFirestore(entry));
        console.log(`Updated news entry ${entryId}`);
    }

    /**
     * Replace the entry's image in Cloud Storage and return its download URL
     * (store it in imageUrl). Each upload gets a fresh file name, so the URL
     * changes and clients don't keep showing a cached old image.
     */
    static async uploadImage(entryId: string, image: Blob): Promise<string> {
        await this.deleteImage(entryId);
        const imageRef = ref(storage, `${this.COLLECTION_NAME}/${entryId}/image-${Date.now()}.jpg`);
        await uploadBytes(imageRef, image, { contentType: 'image/jpeg' });
        return await getDownloadURL(imageRef);
    }

    /** Delete every stored file of the entry (its image). */
    static async deleteImage(entryId: string): Promise<void> {
        const folder = await listAll(ref(storage, `${this.COLLECTION_NAME}/${entryId}`));
        await Promise.all(folder.items.map(item => deleteObject(item)));
    }

    /** Delete a news entry document and its storage files. */
    static async deleteNewsEntry(entryId: string): Promise<void> {
        await this.deleteImage(entryId);
        await deleteDoc(doc(firestore, this.COLLECTION_NAME, entryId));
        console.log(`Deleted news entry ${entryId}`);
    }

    /**
     * Subscribe to news entries. With includeDrafts=true, returns all entries
     * (public + non-public); admin Firestore rules required. With false, only public
     * entries. Returns an Unsubscribe.
     */
    static subscribeNewsEntries(
        includeDrafts: boolean,
        onUpdate: (entries: DatabaseNewsEntry[]) => void,
        onError?: (error: Error) => void,
    ): Unsubscribe {
        const q = includeDrafts
            ? query(this._newsCollection)
            : query(this._newsCollection, where(FieldPaths.PUBLIC, '==', true));
        return onSnapshot(
            q,
            (snapshot) => {
                const entries: DatabaseNewsEntry[] = [];
                snapshot.forEach((doc) => {
                    try {
                        entries.push(this.fromFirestore(doc.data(), doc.id));
                    } catch (error) {
                        console.error(`Error parsing news entry ${doc.id}:`, error);
                    }
                });
                onUpdate(entries);
            },
            (error) => {
                console.error('Error in news entries subscription:', error);
                onError?.(error);
            },
        );
    }
}
